import { Link } from 'react-router-dom';
import { RemoteImage } from '../components/RemoteImage.js';
import { formatEventDates, timezoneLabel } from '../lib/dates.js';
import {
  type SiteSponsor,
  type SiteSponsorTier,
  groupSponsorsByTier,
  sectionState,
} from '../lib/site.js';
import { useDocumentTitle } from '../lib/useDocumentTitle.js';
import { useSite } from './EventLayout.js';

/**
 * The event's front page — name, dates, location, description, and a way into
 * whichever sections the organisers have published.
 *
 * Deliberately minimal (design §7.1). The signal it carries beyond the header
 * is *what exists*: a card appears for Schedule or Speakers only when that
 * section arrived in the payload, so the home page is an honest index of what
 * an attendee can actually reach.
 */
export function EventHome() {
  const payload = useSite();
  useDocumentTitle(payload.event.name);
  const base = `/e/${encodeURIComponent(payload.event.slug)}`;
  const sessions = sectionState(payload.sessions);
  const speakers = sectionState(payload.speakers);
  const sponsors = sectionState(payload.sponsors);

  const dates = formatEventDates(
    payload.event.startAt,
    payload.event.endAt,
    payload.event.timezone,
  );

  return (
    <div className="animate-fade-up space-y-8">
      <section className="e67-card px-6 py-7 sm:px-8 sm:py-8">
        <h2 className="e67-eyebrow">The event</h2>
        <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
          <Fact label="Dates" value={dates} />
          <Fact label="Location" value={payload.event.location} />
        </dl>
        {payload.event.description && (
          <p className="e67-prose mt-6 max-w-reading text-pretty">{payload.event.description}</p>
        )}
        <p className="mt-6 text-[12px] text-brand-muted">{timezoneLabel(payload.event.timezone)}</p>
      </section>

      {(sessions.kind !== 'hidden' || speakers.kind !== 'hidden') && (
        <section aria-labelledby="explore">
          <h2 id="explore" className="e67-eyebrow">
            Explore
          </h2>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {sessions.kind !== 'hidden' && (
              <NavCard
                to={`${base}/sessions`}
                title="Schedule"
                detail={
                  sessions.kind === 'empty'
                    ? 'Nothing scheduled yet'
                    : `${sessions.items.length} ${sessions.items.length === 1 ? 'session' : 'sessions'}`
                }
              />
            )}
            {speakers.kind !== 'hidden' && (
              <NavCard
                to={`${base}/speakers`}
                title="Speakers"
                detail={
                  speakers.kind === 'empty'
                    ? 'To be announced'
                    : `${speakers.items.length} ${speakers.items.length === 1 ? 'speaker' : 'speakers'}`
                }
              />
            )}
          </div>
        </section>
      )}

      {sponsors.kind === 'ready' && (
        <Sponsors sponsors={sponsors.items} tiers={payload.sponsorTiers} />
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div>
      <dt className="e67-eyebrow">{label}</dt>
      <dd className="mt-1 text-[15px] text-brand-on-surface">{value}</dd>
    </div>
  );
}

function NavCard({ to, title, detail }: { to: string; title: string; detail: string }) {
  return (
    <Link
      to={to}
      className="e67-card group flex items-center justify-between gap-4 px-5 py-5 transition hover:bg-brand-elevate"
    >
      <span>
        <span className="e67-title block text-[17px]">{title}</span>
        <span className="mt-0.5 block text-[13px] text-brand-muted">{detail}</span>
      </span>
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5 shrink-0 text-brand-muted transition group-hover:translate-x-0.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="m9 5 7 7-7 7" />
      </svg>
    </Link>
  );
}

/**
 * Grouped under the organiser's tier headings when the backend sends them, in
 * its order and with its labels; the flat strip it always was otherwise.
 */
function Sponsors({
  sponsors,
  tiers,
}: {
  sponsors: SiteSponsor[];
  tiers: SiteSponsorTier[] | undefined;
}) {
  const groups = groupSponsorsByTier(sponsors, tiers);
  return (
    <section aria-labelledby="sponsors">
      <h2 id="sponsors" className="e67-eyebrow">
        Sponsors
      </h2>
      {groups ? (
        <div className="mt-3 space-y-4">
          {groups.map((group) => (
            <div key={group.key} data-sponsor-tier={group.key}>
              <h3 className="text-[13px] font-medium text-brand-muted">{group.label}</h3>
              <SponsorList sponsors={group.sponsors} />
            </div>
          ))}
        </div>
      ) : (
        <SponsorList sponsors={sponsors} />
      )}
    </section>
  );
}

function SponsorList({ sponsors }: { sponsors: SiteSponsor[] }) {
  return (
    <>
      <ul className="mt-3 flex flex-wrap gap-2">
        {sponsors.map((sponsor) => {
          const href =
            sponsor.websiteUrl && /^https?:\/\//i.test(sponsor.websiteUrl)
              ? sponsor.websiteUrl
              : null;
          const body = (
            <span className="flex items-center gap-2.5">
              <RemoteImage
                src={sponsor.logoUrl}
                alt=""
                className="h-6 w-auto max-w-24 object-contain"
                fallback={null}
              />
              <span className="text-[14px] font-medium text-brand-on-surface">{sponsor.name}</span>
            </span>
          );
          return (
            <li key={sponsor.id}>
              {href ? (
                <a
                  href={href}
                  // noreferrer as well as noopener: the attendee site is reached
                  // from token-bearing URLs, and a sponsor's analytics has no
                  // business seeing where a visitor came from.
                  rel="noopener noreferrer"
                  target="_blank"
                  className="e67-card inline-flex px-4 py-2.5 transition hover:bg-brand-elevate"
                >
                  {body}
                </a>
              ) : (
                <span className="e67-card inline-flex px-4 py-2.5">{body}</span>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}
